import React, { useId, useRef } from 'react';
import styled from '@emotion/styled';
import { ActiveIndicator, IndicatorGroup, SCALE } from '../../motion';
import {
  duration,
  easing,
  layout,
  palette,
  radius,
  shadow,
  spacing,
  typography,
} from '../../style-utils/theme';

/**
 * ONE OF A FEW — the generic segmented pill.
 *
 * The billing IntervalToggle generalised: the same radio group drawn as the
 * product's segmented pill, with ONE lagoon indicator that moves between
 * options (IndicatorGroup + ActiveIndicator) instead of being redrawn on each.
 * IntervalToggle itself is left alone; this takes any `options` list.
 *
 * KEYBOARD, like a native radio group: one tab stop (roving tabindex — only the
 * checked option is in the tab order), the arrows move AND select, Home and End
 * jump to the ends. Left/Up go back, Right/Down go forward, both wrap. An
 * option marked `disabled` is skipped by all of them.
 *
 * 44px tall everywhere, the height IntervalToggle already ships — a mouse gets
 * the same target a thumb needs.
 */

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  /** This one option cannot be chosen (the caller says why, beside the
   *  control). The arrows step over it. */
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string> {
  options: ReadonlyArray<SegmentOption<T>>;
  value: T;
  onChange: (next: T) => void;
  /** Accessible name of the radio group. */
  label: string;
  disabled?: boolean;
  className?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  disabled = false,
  className,
}: SegmentedControlProps<T>): React.ReactElement {
  const uid = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const checkedIndex = options.findIndex(option => option.value === value);
  // Nothing checked still leaves ONE way in: the first option takes the stop.
  const tabStop = checkedIndex === -1 ? 0 : checkedIndex;

  const select = (index: number) => {
    onChange(options[index].value);
    refs.current[index]?.focus();
  };

  /* The first option that can be chosen, looking from `from` in steps of
     `step`. -1 when every option is disabled. */
  const enabledFrom = (from: number, step: 1 | -1): number => {
    const count = options.length;
    for (let i = 0; i < count; i += 1) {
      const at = (((from + i * step) % count) + count) % count;
      if (!options[at].disabled) return at;
    }

    return -1;
  };

  const onKeyDown = (event: React.KeyboardEvent, index: number) => {
    const count = options.length;
    let next: number;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = enabledFrom(index + 1, 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = enabledFrom(index - 1, -1);
        break;
      case 'Home':
        next = enabledFrom(0, 1);
        break;
      case 'End':
        next = enabledFrom(count - 1, -1);
        break;
      default:
        return;
    }
    event.preventDefault();
    if (next >= 0) select(next);
  };

  return (
    <IndicatorGroup id={`${uid}-segments`}>
      <Group role="radiogroup" aria-label={label} className={className}>
        {options.map((option, index) => {
          const checked = index === checkedIndex;

          return (
            <Option
              key={option.value}
              ref={el => {
                refs.current[index] = el;
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={index === tabStop ? 0 : -1}
              disabled={disabled || option.disabled === true}
              $checked={checked}
              onClick={() => onChange(option.value)}
              onKeyDown={event => onKeyDown(event, index)}
            >
              {checked && <Indicator id={`${uid}-segments-pill`} />}
              <OptionLabel>{option.label}</OptionLabel>
            </Option>
          );
        })}
      </Group>
    </IndicatorGroup>
  );
}

const Group = styled.div`
  display: inline-flex;
  align-self: flex-start;
  gap: ${spacing.half};
  max-width: 100%;
  min-width: 0;
  padding: ${spacing.half};
  background: ${palette.surfaceMuted};
  border-radius: ${radius.pill};
`;

type OptionProps = { $checked: boolean };

const optionColor = (p: OptionProps) => {
  return p.$checked ? palette.surface : palette.slate;
};
const optionHoverColor = (p: OptionProps) => {
  return p.$checked ? palette.surface : palette.ink;
};

/* The label weight is held constant in both states: a weight swap reflows the
   width the moving pill is sliding under. The indicator is the option's first
   child and the label is positioned after it, so the label paints above the
   pill by source order and no z-index is needed. */
const Option = styled.button<OptionProps>`
  -webkit-appearance: none;
  appearance: none;
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  min-height: ${layout.touchTarget};
  padding: ${spacing.xs} ${spacing.sm};
  border: none;
  border-radius: ${radius.pill};
  background: transparent;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  color: ${optionColor};
  cursor: pointer;
  transition: color ${duration.fast} ${easing.standard},
    box-shadow ${duration.base} ${easing.standard},
    transform ${duration.instant} ${easing.spring};

  &:hover:not(:disabled) {
    color: ${optionHoverColor};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: scale(${SCALE.pressed});
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

/* Wraps only when a label cannot fit at all, so a long option at 320 grows
   the pill downward instead of pushing the page sideways. */
const OptionLabel = styled.span`
  position: relative;
  display: block;
  text-align: center;
  overflow-wrap: anywhere;
`;

const Indicator = styled(ActiveIndicator)`
  position: absolute;
  inset: 0;
  border-radius: ${radius.pill};
  background: ${palette.lagoon};
  box-shadow: ${shadow.xs};
`;
